import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActionArea,
  CardMedia,
  Container,
  Divider,
  Fab,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  SvgIcon,
  SvgIconProps,
  TextField,
  Typography
} from '@material-ui/core';
import {
  createStyles,
  fade,
  makeStyles,
  Theme
} from '@material-ui/core/styles';
import {
  ArrowBackRounded,
  EmailOutlined,
  KeyboardArrowRightRounded,
  SettingsBrightnessOutlined
} from '@material-ui/icons';
import CameraAltIcon from '@material-ui/icons/CameraAltRounded';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import clsx from 'clsx';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Link as RRLink, Switch, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColorModeContext } from '../..';
import { UpdatePassword } from '../../api/external-users';
import { GetForgotPasswordParams } from '../../api/reset-password-external-users';
import { RootState } from '../../app/store';
import { stringToColor } from '../../functions/stringAvatar';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useCoverPic from '../../hooks/useCoverPic';
import { getProfilePic } from '../Auth/authSCG';
import { updatePasswordExternalUserAsync } from '../ExternalUser/externalUserSlice';
import loadingSlice from '../Loading/loadingSlice';
import { getForgotPasswordAsync } from '../ResetPassword/resetPasswordSlice';
import Notification from './Notification';

type DateFormat = 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd';

interface Password {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

interface DateFormatOption {
  format: DateFormat;
  label: string;
  description: string;
  example: string;
  displayFormat?: string; // For Material-UI DatePicker
  placeholder?: string; // For placeholder
}

const DATE_FORMATS: DateFormatOption[] = [
  {
    format: 'dd/MM/yyyy',
    label: 'DD/MM/YYYY',
    description: 'Day first (common in Europe, Asia)',
    example: moment().format('DD/MM/YYYY'),
    displayFormat: 'dd/MM/yyyy',
    placeholder: 'dd/mm/yyyy'
  },
  {
    format: 'MM/dd/yyyy',
    label: 'MM/DD/YYYY',
    description: 'Month first (common in USA)',
    example: moment().format('MM/DD/YYYY'),
    displayFormat: 'MM/dd/yyyy',
    placeholder: 'mm/dd/yyyy'
  },
  {
    format: 'yyyy-MM-dd',
    label: 'YYYY-MM-DD',
    description: 'ISO 8601 standard (sortable)',
    example: moment().format('YYYY-MM-DD'),
    displayFormat: 'yyyy-MM-dd',
    placeholder: 'yyyy-mm-dd'
  }
];

const STORAGE_KEY = 'userDateFormat';
const DEFAULT_FORMAT: DateFormat = 'dd/MM/yyyy';

// Helper Functions localStorage
// - get date format from localStorage
// - if no -> return default format
// - if yes -> return stored format
// - if invalid -> return default format
export function getDateFormatFromStorage(): DateFormat {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      console.log('No date format found, setting default:', DEFAULT_FORMAT);
      saveDateFormatToStorage(DEFAULT_FORMAT);
      return DEFAULT_FORMAT;
    }

    const validFormats = DATE_FORMATS.map((f) => f.format);
    if (stored && validFormats.includes(stored as DateFormat)) {
      return stored as DateFormat;
    }
  } catch (error) {
    console.warn('Failed to read date format from localStorage:', error);
  }

  return DEFAULT_FORMAT;
}

// Save date format to localStorage
function saveDateFormatToStorage(format: DateFormat): void {
  try {
    localStorage.setItem(STORAGE_KEY, format);

    // send event to notify other parts of the app about the change
    window.dispatchEvent(
      new CustomEvent('dateFormatChanged', {
        detail: { format }
      })
    );
  } catch (error) {
    console.error('Failed to save date format to localStorage:', error);
    throw error;
  }
}

// Get date format option
export function getDateFormatOption(
  format: DateFormat
): DateFormatOption | undefined {
  return DATE_FORMATS.find((f) => f.format === format);
}

export default function Account() {
  const { path } = useRouteMatch();
  return (
    <Switch>
      <Route exact path={`${path}/notifications`}>
        <Notification />
      </Route>
      <Route exact path={path}>
        <AccountPage />
      </Route>
    </Switch>
  );
}

function AccountPage() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const { user } = useSelector((state: RootState) => state.userProfile);
  const isExternalUser = !user?.email.includes('scg.com');

  const [password, setPassword] = useState<Password>({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const [changePassword, setChangePassword] = useState<boolean>(false);
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const disabledSubmit =
    (password.oldPassword.length === 0 ||
      password.newPassword.length < 8 ||
      password.newPassword !== password.confirmNewPassword) &&
    !forgotPassword;
  const showError =
    password.confirmNewPassword.length > 0 &&
    password.newPassword !== password.confirmNewPassword;

  const [profilePic, setProfilePic] = useState<string | null>(getProfilePic());
  const [selectedFormat, setSelectedFormat] = useState<DateFormat>(
    getDateFormatFromStorage()
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        const newFormat = event.newValue as DateFormat;
        setSelectedFormat(newFormat);
        console.log('Date format updated from storage event:', selectedFormat);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [selectedFormat]);

  const coverPic = useCoverPic();

  const colorMode = useContext(ColorModeContext);
  const themeButtons = [
    {
      text: t('account.themeLight'),
      value: 'light',
      icon: <LightModeRounded className={classes.toggleIcon} fontSize="small" />
    },
    {
      text: 'Dark',
      value: 'dark',
      icon: <DarkModeOutlined className={classes.toggleIcon} fontSize="small" />
    },
    {
      text: 'System',
      value: 'system',
      icon: (
        <SettingsBrightnessOutlined
          className={classes.toggleIcon}
          fontSize="small"
        />
      )
    }
  ];

  const handleDateFormatChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newFormat = event.target.value as DateFormat;

    try {
      // 1. บันทึกลง localStorage ทันที
      saveDateFormatToStorage(newFormat);

      // 2. อัพเดท state
      setSelectedFormat(newFormat);

      // 3. แสดง notification
      setSnackbar({
        open: true,
        message: 'Date format changed successfully!',
        severity: 'success'
      });

      // Optional: Reload หน้าเพื่อให้ DatePicker อัพเดท
      // setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      // ถ้า error
      setSnackbar({
        open: true,
        message: 'Failed to save date format',
        severity: 'error'
      });
    }
  };

  function handleChangeTheme(
    event: React.MouseEvent<HTMLElement>,
    newTheme: 'light' | 'system' | 'dark'
  ) {
    if (newTheme) {
      colorMode.selectColorMode(newTheme);
    }
  }

  function handleChangeLanguage(
    event: React.MouseEvent<HTMLElement>,
    newLanguage: 'th' | 'en'
  ) {
    if (newLanguage) {
      i18n.changeLanguage(newLanguage);
    }
  }

  function imageUpload(event: any) {
    const file = event.target.files[0];
    getBase64(file).then((base64: any) => {
      setProfilePic(base64);
      localStorage['profilePic'] = base64;
    });
  }

  function getBase64(file: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      if (file) {
        reader.readAsDataURL(file);
      }
    });
  }

  function handleChangePassword(open: boolean) {
    return function () {
      if (open) {
        setChangePassword(true);
      } else {
        setPassword({
          oldPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        });
        setChangePassword(false);
        setForgotPassword(false);
      }
    };
  }

  function onClickForgotPassword() {
    setForgotPassword((prev) => !prev);
  }

  function handleChangeTextField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setPassword((prev) => ({ ...prev, [name]: value }));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      if (forgotPassword) {
        const email = user?.email;
        if (email) {
          const forgotPassword: GetForgotPasswordParams = {
            email
          };
          await dispatch(getForgotPasswordAsync(forgotPassword)).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('login.resetEmailSentMessage') as string
            })
          );
          handleChangePassword(false)();
        } else {
          throw new Error(t('account.emailInvalid') as string);
        }
      } else {
        const updatePassword: UpdatePassword = {
          oldPassword: password.oldPassword,
          newPassword: password.newPassword
        };
        await dispatch(
          updatePasswordExternalUserAsync(updatePassword)
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('account.passwordChangedSuccess') as string
          })
        );
        handleChangePassword(false)();
      }
    } catch (error) {
      if (error instanceof Error)
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
    } finally {
      setPassword({ oldPassword: '', newPassword: '', confirmNewPassword: '' });
    }
  }

  return (
    <div className={classes.root}>
      <Container className={classes.container} maxWidth="sm">
        <CardMedia
          className={classes.media}
          image={coverPic!}
          component={Paper}
          elevation={0}
        />
        <div className={classes.avatar__flex}>
          <Avatar
            className={classes.avatar}
            style={{
              backgroundColor: stringToColor(
                `${user?.firstName} ${user?.lastName}`
              )
            }}
            src={profilePic!}
          >
            {user?.firstName[0]}
            {user?.lastName[0]}
          </Avatar>
          <label htmlFor="contained-button-file">
            <Fab
              className={classes.button__addPic}
              size="small"
              color="primary"
              aria-label="add"
              component="span"
            >
              <CameraAltIcon />
            </Fab>
          </label>
        </div>
        <Card className={classes.profile__box}>
          <Typography
            className={classes.profile__name}
            component="p"
            variant="h5"
          >
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography component="p" variant="body2" color="textSecondary">
            {user?.email}
          </Typography>
        </Card>
        <input
          accept=".jpg"
          id="contained-button-file"
          type="file"
          style={{ display: 'none' }}
          onChange={imageUpload}
        />
        <Paper className={classes.card}>
          <Typography variant="subtitle1" gutterBottom>
            {t('account.settings')}
          </Typography>
          <Divider className={classes.divider} />
          {isExternalUser && (
            <div className={classes.mb2}>
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
                gutterBottom
              >
                {t('account.sectionAccount')}
              </Typography>
              {!changePassword && (
                <ButtonGroup orientation="vertical" disableRipple fullWidth>
                  <Button
                    className={classes.menuButton}
                    variant="text"
                    endIcon={<KeyboardArrowRightRounded />}
                    onClick={handleChangePassword(true)}
                    fullWidth
                  >
                    {t('account.changePassword')}
                  </Button>
                </ButtonGroup>
              )}
              {changePassword && (
                <form onSubmit={onSubmit}>
                  <div className={clsx(classes.flexAlignCenter, classes.mb1)}>
                    <IconButton
                      size="small"
                      edge="start"
                      onClick={handleChangePassword(false)}
                    >
                      <ArrowBackRounded />
                    </IconButton>
                    <Typography>
                      {forgotPassword
                        ? t('account.resetPassword')
                        : t('account.changePassword')}
                    </Typography>
                  </div>
                  {!forgotPassword && (
                    <>
                      <Typography className={classes.mb2} color="textSecondary">
                        {t('account.passwordRules')}
                      </Typography>
                      <TextField
                        className={classes.mb1}
                        autoComplete="current-password"
                        type="password"
                        name="oldPassword"
                        variant="outlined"
                        size="small"
                        placeholder={t('account.currentPasswordPlaceholder') as string}
                        value={password.oldPassword}
                        onChange={handleChangeTextField}
                        required
                        fullWidth
                      />
                      <TextField
                        className={classes.mb1}
                        autoComplete="new-password"
                        type="password"
                        name="newPassword"
                        variant="outlined"
                        size="small"
                        placeholder={t('account.newPasswordPlaceholder') as string}
                        value={password.newPassword}
                        onChange={handleChangeTextField}
                        required
                        fullWidth
                      />
                      <TextField
                        autoComplete="new-password"
                        type="password"
                        name="confirmNewPassword"
                        variant="outlined"
                        size="small"
                        placeholder={t('account.confirmNewPasswordPlaceholder') as string}
                        value={password.confirmNewPassword}
                        onChange={handleChangeTextField}
                        error={showError}
                        helperText={
                          showError
                            ? (t('account.passwordMismatchHelper') as string)
                            : ''
                        }
                        required
                        fullWidth
                      />
                    </>
                  )}
                  {forgotPassword && (
                    <Typography
                      className={classes.forgotText}
                      variant="body2"
                    >{t('account.forgotDescription', { email: user?.email })}</Typography>
                  )}
                  <Button
                    className={classes.forgotButton}
                    disableRipple
                    onClick={onClickForgotPassword}
                  >
                    {forgotPassword
                      ? t('account.toggleToChange')
                      : t('account.toggleToForgot')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={disabledSubmit}
                    disableElevation
                    fullWidth
                  >
                    {forgotPassword
                      ? t('account.sendResetLink')
                      : t('account.changePassword')}
                  </Button>
                </form>
              )}
            </div>
          )}
          <Typography
            variant="caption"
            display="block"
            color="textSecondary"
            gutterBottom
          >
            {t('account.mode')}
          </Typography>
          <ToggleButtonGroup
            className={`${classes.toggleGroupButton} ${classes.mb2}`}
            value={colorMode.currentTheme}
            exclusive
            onChange={handleChangeTheme}
            size="small"
          >
            {themeButtons.map((themeButton) => {
              return (
                <ToggleButton
                  className={classes.toggleButton}
                  value={themeButton.value}
                  key={themeButton.text}
                >
                  {themeButton.icon}
                  {themeButton.text}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          <Divider style={{ margin: '20px 0' }} />

          {/* Date Format Section */}
          <Box>
            <Typography
              variant="caption"
              display="block"
              color="textPrimary"
              gutterBottom
            >
              DATE FORMAT
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 16 }}
            >
              Choose how dates are displayed across the app
            </Typography>
            <RadioGroup
              value={selectedFormat}
              onChange={handleDateFormatChange}
            >
              {DATE_FORMATS.map((option) => {
                const isSelected = selectedFormat === option.format;
                return (
                  <Box
                    key={option.format}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    padding="12px 16px"
                    border="1px solid"
                    borderColor={isSelected ? '#3b82f6' : '#e5e7eb'}
                    borderRadius={8}
                    style={{ cursor: 'pointer', marginBottom: 12 }}
                    onClick={() => setSelectedFormat(option.format)}
                  >
                    <Box display="flex" alignItems="center" gridGap={12}>
                      <FormControlLabel
                        value={option.format}
                        control={<Radio color="primary" />}
                        label=""
                      />
                      {/* <Radio value={option.format} size="small" /> */}
                      <Box>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </RadioGroup>

            {/* Example Date */}
            <Box>
              <Box
                style={{
                  marginTop: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8
                }}
              >
                <Typography variant="body2">
                  <span style={{ fontWeight: 600 }}>Example Date:</span>{' '}
                  <span style={{ fontFamily: 'monospace', color: '#3b82f6' }}>
                    {getDateFormatOption(selectedFormat)?.example}
                  </span>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Your selected format would appear as shown above
                </Typography>
              </Box>
            </Box>
          </Box>
          {false && ( //TODO: Remove "false" to show this part if email config page is completed
            <Card variant="outlined">
              <CardActionArea
                className={classes.menuCard}
                component={RRLink}
                to="/account/notifications"
              >
                <EmailOutlined color="primary" />
                <div className={classes.menuTextBox}>
                  <Typography className={classes.menuText} variant="h6">
                    {t('account.emailNotificationTitle')}
                  </Typography>
                  <Typography
                    className={classes.menuHelpText}
                    variant="body2"
                    color="textSecondary"
                  >
                    {t('account.emailNotificationConfigure')}{' '}
                    <span className={classes.logo}>backspace</span>
                  </Typography>
                </div>
                <KeyboardArrowRightRounded color="action" />
              </CardActionArea>
            </Card>
          )}
        </Paper>
      </Container>
    </div>
  );
}

function LightModeRounded(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
    </SvgIcon>
  );
}

function DarkModeOutlined(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M9.37,5.51C9.19,6.15,9.1,6.82,9.1,7.5c0,4.08,3.32,7.4,7.4,7.4c0.68,0,1.35-0.09,1.99-0.27C17.45,17.19,14.93,19,12,19 c-3.86,0-7-3.14-7-7C5,9.07,6.81,6.55,9.37,5.51z M12,3c-4.97,0-9,4.03-9,9s4.03,9,9,9s9-4.03,9-9c0-0.46-0.04-0.92-0.1-1.36 c-0.98,1.37-2.58,2.26-4.4,2.26c-2.98,0-5.4-2.42-5.4-5.4c0-1.81,0.89-3.42,2.26-4.4C12.92,3.04,12.46,3,12,3L12,3z" />
    </SvgIcon>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3, 0, 10, 0)
    },
    container: {
      padding: 0
    },
    avatar__flex: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    avatar: {
      width: theme.spacing(15),
      height: theme.spacing(15),
      marginTop: theme.spacing(-14),
      backgroundColor: theme.palette.primary.dark,
      borderStyle: 'solid',
      borderColor: theme.palette.background.paper,
      borderWidth: theme.spacing(0.4),
      fontSize: 56,
      fontFamily: 'Oswald'
    },
    button__addPic: {
      position: 'relative',
      top: -40,
      left: 40
    },
    profile__box: {
      display: 'block',
      padding: theme.spacing(3.5, 0, 2, 0),
      margin: theme.spacing(-8, 0, 1, 0),
      textAlign: 'center',
      borderRadius: '16px'
    },
    profile__name: {
      padding: theme.spacing(0, 2),
      lineHeight: '100%'
    },
    media: {
      height: theme.spacing(25),
      backgroundColor: fade(theme.palette.background.paper, 0.6),
      borderRadius: '16px 16px 0 0'
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    menuCard: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: theme.spacing(1, 1.5)
    },
    menuTextBox: {
      flexGrow: 1,
      margin: theme.spacing(0, 1.5)
    },
    menuText: {
      lineHeight: '120%'
    },
    menuHelpText: {
      lineHeight: '120%',
      fontSize: 12
    },
    logo: {
      fontFamily: 'Comfortaa',
      fontWeight: 700,
      letterSpacing: -0.5
    },
    toggleGroupButton: {
      display: 'flex'
    },
    toggleButton: {
      width: `calc(${100 / 3}%)`,
      textTransform: 'unset',
      color:
        theme.palette.type === 'light'
          ? theme.palette.text.secondary
          : theme.palette.text.primary,
      '&.Mui-selected': {
        color: theme.palette.primary.main,
        backgroundColor: fade(theme.palette.primary.main, 0.08)
      },
      '&.Mui-selected:hover': {
        backgroundColor: fade(theme.palette.primary.main, 0.16)
      },
      // For language toggle (2 buttons)
      '&.language-toggle': {
        width: `calc(${100 / 2}%)`
      }
    },
    toggleIcon: {
      marginRight: theme.spacing(0.5)
    },
    menuButton: {
      textTransform: 'unset',
      color:
        theme.palette.type === 'light'
          ? theme.palette.text.secondary
          : theme.palette.text.primary,
      borderRadius: 4,
      display: 'flex',
      justifyContent: 'space-between'
    },
    forgotButton: {
      textTransform: 'none',
      textDecoration: 'underline',
      marginBottom: theme.spacing(1),
      '&:hover, &.Mui-focusVisible': {
        color: theme.palette.primary.main,
        backgroundColor: 'transparent'
      }
    },
    forgotText: {
      fontSize: 12,
      marginLeft: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.secondary
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5)
    },
    unset: {
      textTransform: 'unset'
    },
    mb1: {
      marginBottom: theme.spacing(1)
    },
    mb2: {
      marginBottom: theme.spacing(2)
    }
  })
);
